import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Import the separate form components
import ProfileInformationForm from '../therapist-dashboard/ProfileInformationForm';
import PracticeDetailsForm from '../therapist-dashboard/PracticeDetailsForm';
import LocationCommunityForm from '../therapist-dashboard/LocationCommunityForm';
import EmbedLogoCard from '../therapist-dashboard/EmbedLogoCard';
import AccountSettings from '../therapist-dashboard/AccountSettings';

function TherapistDashboard() {
  const navigate = useNavigate();
  const [therapist, setTherapist] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [validationErrors, setValidationErrors] = useState({});
  const [inquiries, setInquiries] = useState([]);
  const [isLoadingInquiries, setIsLoadingInquiries] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [inquirySearchTerm, setInquirySearchTerm] = useState('');
  const [inquiryFilter, setInquiryFilter] = useState('all');
  const [showEmbedPromo, setShowEmbedPromo] = useState(false);
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
  const [inviteMenuOpen, setInviteMenuOpen] = useState(false);
  const inviteMenuRef = useRef(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    title: '',
    professionalType: '',
    credentials: [],
    pronouns: '',
    gender: '',
    email: '',
    phone: '',
    website: '',
    practiceName: '',
    bio: '',
    profilePhoto: '',
    profileVideo: '',
    gallery: [], // Array of image URLs for photo gallery
    location: {
      address: '',
      city: '',
      state: '',
      zipCode: '',
      serviceRadius: 50
    },
          serviceAreas: {
        zipCodes: [],
        zipCodesRaw: '', // Raw text for typing experience
        states: [],
        virtualOnly: false,
        inPersonOnly: false,
        hybrid: true
      },
    professional: {
      licenseNumber: '',
      npiNumber: '',
      yearsExperience: '',
      education: [],
      certifications: [],
      languages: [],
      licenses: [],
      awards: []
    },
    communityFocus: {
      lgbtqAllied: false,
      racialJusticeAllied: false,
      bodyPositive: false,
      traumaInformed: false,
      neurodiversityAffirming: false,
      faithBased: false,
      religiousAffiliations: [],
      culturalSpecialties: [],
      accessibilityFeatures: []
    },
    specializations: [],
    treatmentApproaches: [],
    ageGroups: [],
    sessionTypes: [],
    insuranceAccepted: [],
    rates: {
      individual: '',
      couples: '',
      family: '',
      group: '',
      slidingScale: false,
      freeConsultation: {
        offered: false
      }
    },
    paymentMethods: {
      acceptsCash: false,
      acceptsCheck: false,
      acceptsVisa: false,
      acceptsMastercard: false,
      acceptsAmericanExpress: false,
      acceptsDiscover: false,
      acceptsPayPal: false,
      acceptsVenmo: false,
      acceptsZelle: false,
      acceptsHSA: false,
      acceptsFSA: false,
      other: []
    },
          insurance: {
        acceptsInsurance: false,
        inNetwork: [],
        outOfNetwork: false,
        providesSuperbill: false,
        superbillDescription: '',
        priorAuthRequired: false
      },
      acceptingNewClients: true
    });

  // Step configuration
  const steps = [
    {
      id: 1,
      title: 'Profile Information',
      description: 'Basic info, credentials & media',
      icon: '👤',
      component: ProfileInformationForm
    },
    {
      id: 2,
      title: 'Practice Details',
      description: 'Specializations, approaches & rates',
      icon: '🏥',
      component: PracticeDetailsForm
    },
    {
      id: 3,
      title: 'Location & Community',
      description: 'Service areas & focus',
      icon: '📍',
      component: LocationCommunityForm
    }
  ];

  useEffect(() => {
    checkAuthAndLoadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (therapist && !isEditing) {
      // Add a small delay to prevent rapid-fire API calls
      const timeoutId = setTimeout(() => {
        loadInquiries();
        loadAnalyticsData();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapist?._id, isEditing]); // Use therapist._id instead of full object to prevent unnecessary re-renders

  useEffect(() => {
    if (therapist?._id && !isEditing && !localStorage.getItem('therapylist_embed_promo_seen')) {
      const timeoutId = setTimeout(() => setShowEmbedPromo(true), 1500);
      return () => clearTimeout(timeoutId);
    }
  }, [therapist?._id, isEditing]);

  useEffect(() => {
    if (!inviteMenuOpen) return;
    const handleClickOutside = (e) => {
      if (inviteMenuRef.current && !inviteMenuRef.current.contains(e.target)) {
        setInviteMenuOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setInviteMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [inviteMenuOpen]);

  const checkAuthAndLoadProfile = async () => {
    const token = localStorage.getItem('therapylist_token');
    const userStr = localStorage.getItem('therapylist_user');
    
    if (!token || !userStr) {
      navigate('/login');
      return;
    }

    let user;
    try {
      user = JSON.parse(userStr);
    } catch (e) {
      localStorage.removeItem('therapylist_user');
      navigate('/login');
      return;
    }
    if (user.role !== 'therapist') {
      navigate('/');
      return;
    }

    await loadTherapistProfile();
  };

  const loadTherapistProfile = async () => {
    try {
      const token = localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/therapists/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to load profile');
      }

      if (data.therapist) {
        setTherapist(data.therapist);
        populateFormData(data.therapist);
      } else {
        setIsEditing(true);
      }
    } catch (error) {
      console.error('Load profile error:', error);
      setError('Failed to load profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadInquiries = async () => {
    setIsLoadingInquiries(true);
    try {
      const token = localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/inquiries/therapist?limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInquiries(data.inquiries || []);
      } else {
        // Handle non-JSON error responses (like 429 Too Many Requests)
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const text = await response.text();
          try {
            const data = JSON.parse(text);
            errorMessage = data.message || errorMessage;
          } catch (jsonError) {
            errorMessage = text || errorMessage;
          }
        } catch (readError) {
          // Could not read response body
        }
        
        console.error('Failed to load inquiries:', errorMessage);
        
        // Show user-friendly error message for rate limiting
        if (response.status === 429) {
          setError('Loading too frequently. Please wait a moment before refreshing.');
        }
      }
    } catch (error) {
      console.error('Load inquiries error:', error);
      setError('Failed to load inquiries. Please try again.');
    } finally {
      setIsLoadingInquiries(false);
    }
  };

  const updateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      const token = localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/inquiries/${inquiryId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();

      if (response.ok) {
        // Update the inquiry in local state
        setInquiries(prev => prev.map(inquiry => 
          inquiry._id === inquiryId 
            ? { ...inquiry, status: newStatus }
            : inquiry
        ));
        setSuccess('Inquiry status updated successfully!');
      } else {
        setError(data.message || 'Failed to update inquiry status');
      }
    } catch (error) {
      console.error('Update inquiry status error:', error);
      setError('Failed to update inquiry status. Please try again.');
    }
  };

  const populateFormData = (therapistData) => {
    setFormData({
      firstName: therapistData.firstName || '',
      lastName: therapistData.lastName || '',
      title: therapistData.title || '',
      professionalType: therapistData.professionalType || '',
      credentials: therapistData.credentials || [],
      pronouns: therapistData.pronouns || '',
      gender: therapistData.gender || '',
      email: therapistData.email || '',
      phone: therapistData.phone || '',
      website: therapistData.website || '',
      practiceName: therapistData.practiceName || '',
      bio: therapistData.bio || '',
      profilePhoto: therapistData.profilePhoto || '',
      profileVideo: therapistData.profileVideo || '',
      gallery: therapistData.additionalPhotos || [], // Backend uses 'additionalPhotos' field
      location: {
        address: therapistData.location?.address || '',
        city: therapistData.location?.city || '',
        state: therapistData.location?.state || '',
        zipCode: therapistData.location?.zipCode || '',
        serviceRadius: therapistData.location?.serviceRadius || 50
      },
      serviceAreas: {
        zipCodes: therapistData.serviceAreas?.zipCodes || [],
        zipCodesRaw: therapistData.serviceAreas?.zipCodes?.join(', ') || '', // Convert array to text for editing
        states: therapistData.serviceAreas?.states || [],
        virtualOnly: therapistData.serviceAreas?.virtualOnly || false,
        inPersonOnly: therapistData.serviceAreas?.inPersonOnly || false,
        hybrid: therapistData.serviceAreas?.hybrid || true
      },
      professional: {
        licenseNumber: therapistData.professional?.licenseNumber || '',
        npiNumber: therapistData.professional?.npiNumber || '',
        yearsExperience: therapistData.professional?.yearsExperience || '',
        education: therapistData.professional?.education || [],
        certifications: therapistData.professional?.certifications || [],
        languages: therapistData.professional?.languages || [],
        licenses: therapistData.professional?.licenses || [],
        awards: therapistData.professional?.awards || []
      },
      communityFocus: {
        lgbtqAllied: therapistData.communityFocus?.lgbtqAllied || false,
        racialJusticeAllied: therapistData.communityFocus?.racialJusticeAllied || false,
        bodyPositive: therapistData.communityFocus?.bodyPositive || false,
        traumaInformed: therapistData.communityFocus?.traumaInformed || false,
        neurodiversityAffirming: therapistData.communityFocus?.neurodiversityAffirming || false,
        faithBased: therapistData.communityFocus?.faithBased || false,
        religiousAffiliations: therapistData.communityFocus?.religiousAffiliations || [],
        culturalSpecialties: therapistData.communityFocus?.culturalSpecialties || [],
        accessibilityFeatures: therapistData.communityFocus?.accessibilityFeatures || []
      },
      specializations: therapistData.specializations || [],
      treatmentApproaches: therapistData.treatmentApproaches || [],
      ageGroups: therapistData.ageGroups || [],
      sessionTypes: therapistData.sessionTypes || [],
      insuranceAccepted: therapistData.insuranceAccepted || [],
      rates: {
        individual: therapistData.rates?.individual || '',
        couples: therapistData.rates?.couples || '',
        family: therapistData.rates?.family || '',
        group: therapistData.rates?.group || '',
        slidingScale: therapistData.rates?.slidingScale || false,
        freeConsultation: {
          offered: therapistData.rates?.freeConsultation?.offered || false
        }
      },
      paymentMethods: {
        acceptsCash: therapistData.paymentMethods?.acceptsCash || false,
        acceptsCheck: therapistData.paymentMethods?.acceptsCheck || false,
        acceptsVisa: therapistData.paymentMethods?.acceptsVisa || false,
        acceptsMastercard: therapistData.paymentMethods?.acceptsMastercard || false,
        acceptsAmericanExpress: therapistData.paymentMethods?.acceptsAmericanExpress || false,
        acceptsDiscover: therapistData.paymentMethods?.acceptsDiscover || false,
        acceptsPayPal: therapistData.paymentMethods?.acceptsPayPal || false,
        acceptsVenmo: therapistData.paymentMethods?.acceptsVenmo || false,
        acceptsZelle: therapistData.paymentMethods?.acceptsZelle || false,
        acceptsHSA: therapistData.paymentMethods?.acceptsHSA || false,
        acceptsFSA: therapistData.paymentMethods?.acceptsFSA || false,
        other: therapistData.paymentMethods?.other || []
      },
      insurance: {
        acceptsInsurance: therapistData.insurance?.acceptsInsurance || false,
        inNetwork: therapistData.insurance?.inNetwork || [],
        outOfNetwork: therapistData.insurance?.outOfNetwork || false,
        providesSuperbill: therapistData.insurance?.providesSuperbill || false,
        superbillDescription: therapistData.insurance?.superbillDescription || '',
        priorAuthRequired: therapistData.insurance?.priorAuthRequired || false
      },
      acceptingNewClients: therapistData.acceptingNewClients !== undefined ? therapistData.acceptingNewClients : true
    });
  };

  // Step-specific validation
  const validateCurrentStep = (stepNumber = currentStep) => {
    const errors = {};

    if (stepNumber === 1) {
      // Step 1: Profile Information Form validation - Only 5 required fields
      
      // Required fields
      if (!formData.firstName.trim()) {
        errors.firstName = 'First name is required';
      }
      if (!formData.lastName.trim()) {
        errors.lastName = 'Last name is required';
      }
      if (!formData.professionalType.trim()) {
        errors.professionalType = 'Professional type is required';
      }
      if (!formData.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
      if (!formData.phone.trim()) {
        errors.phone = 'Phone is required';
      }
      
      // Optional field format validation (only if provided)
      if (formData.bio.trim() && formData.bio.trim().length < 100) {
        errors.bio = 'Bio must be at least 100 characters when provided';
      }
      
      // NPI number format validation (only if provided)
      if (formData.professional.npiNumber.trim() && !/^\d{10}$/.test(formData.professional.npiNumber.replace(/\s/g, ''))) {
        errors.npiNumber = 'NPI number must be exactly 10 digits';
      }
    } else if (stepNumber === 2) {
      // Step 2: Practice Details Form validation (all optional)
      // Validate rates if provided
      if (formData.rates.individual && (isNaN(formData.rates.individual) || parseFloat(formData.rates.individual) < 0 || parseFloat(formData.rates.individual) > 1000)) {
        errors.individualRate = 'Individual rate must be between 0 and 1000';
      }
      if (formData.rates.couples && (isNaN(formData.rates.couples) || parseFloat(formData.rates.couples) < 0 || parseFloat(formData.rates.couples) > 1000)) {
        errors.couplesRate = 'Couples rate must be between 0 and 1000';
      }
      if (formData.rates.family && (isNaN(formData.rates.family) || parseFloat(formData.rates.family) < 0 || parseFloat(formData.rates.family) > 1000)) {
        errors.familyRate = 'Family rate must be between 0 and 1000';
      }
      if (formData.rates.group && (isNaN(formData.rates.group) || parseFloat(formData.rates.group) < 0 || parseFloat(formData.rates.group) > 1000)) {
        errors.groupRate = 'Group rate must be between 0 and 1000';
      }
    } else if (stepNumber === 3) {
      // Step 3: Location & Community Form validation 
      // Only validate location fields when on step 3
      
      // Validate ZIP code if provided
      if (formData.location.zipCode && !/^\d{5}(-\d{4})?$/.test(formData.location.zipCode)) {
        errors.zipCode = 'Please enter a valid ZIP code';
      }
      // Validate service radius if provided
      if (formData.location.serviceRadius && (isNaN(formData.location.serviceRadius) || parseFloat(formData.location.serviceRadius) < 1 || parseFloat(formData.location.serviceRadius) > 100)) {
        errors.serviceRadius = 'Service radius must be between 1 and 100 miles';
      }
    }

    // Only set validation errors for the current step to avoid showing errors for fields not on the current form
    if (stepNumber === currentStep) {
      setValidationErrors(errors);
    }
    
    return Object.keys(errors).length === 0;
  };

  // Full form validation for final save
  const validateForm = () => {
    // Validate all steps and collect all errors
    const allErrors = {};

    // Run validation for each step manually to collect all errors
    [1, 2, 3].forEach(step => {
      const stepErrors = {};
      if (step === 1) {
        if (!formData.firstName.trim()) stepErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) stepErrors.lastName = 'Last name is required';
        if (!formData.professionalType.trim()) stepErrors.professionalType = 'Professional type is required';
        if (!formData.email.trim()) stepErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) stepErrors.email = 'Please enter a valid email address';
        if (!formData.phone.trim()) stepErrors.phone = 'Phone is required';
        if (formData.bio.trim() && formData.bio.trim().length < 100) stepErrors.bio = 'Bio must be at least 100 characters when provided';
        if (formData.professional.npiNumber.trim() && !/^\d{10}$/.test(formData.professional.npiNumber.replace(/\s/g, ''))) stepErrors.npiNumber = 'NPI number must be exactly 10 digits';
      } else if (step === 2) {
        if (formData.rates.individual && (isNaN(formData.rates.individual) || parseFloat(formData.rates.individual) < 0 || parseFloat(formData.rates.individual) > 1000)) stepErrors.individualRate = 'Individual rate must be between 0 and 1000';
        if (formData.rates.couples && (isNaN(formData.rates.couples) || parseFloat(formData.rates.couples) < 0 || parseFloat(formData.rates.couples) > 1000)) stepErrors.couplesRate = 'Couples rate must be between 0 and 1000';
        if (formData.rates.family && (isNaN(formData.rates.family) || parseFloat(formData.rates.family) < 0 || parseFloat(formData.rates.family) > 1000)) stepErrors.familyRate = 'Family rate must be between 0 and 1000';
        if (formData.rates.group && (isNaN(formData.rates.group) || parseFloat(formData.rates.group) < 0 || parseFloat(formData.rates.group) > 1000)) stepErrors.groupRate = 'Group rate must be between 0 and 1000';
      } else if (step === 3) {
        if (formData.location.zipCode && !/^\d{5}(-\d{4})?$/.test(formData.location.zipCode)) stepErrors.zipCode = 'Please enter a valid ZIP code';
        if (formData.location.serviceRadius && (isNaN(formData.location.serviceRadius) || parseFloat(formData.location.serviceRadius) < 1 || parseFloat(formData.location.serviceRadius) > 100)) stepErrors.serviceRadius = 'Service radius must be between 1 and 100 miles';
      }
      Object.assign(allErrors, stepErrors);
    });

    setValidationErrors(allErrors);
    return Object.keys(allErrors).length === 0;
  };

  // eslint-disable-next-line no-unused-vars
  const handleSave = async () => {
    if (!validateForm()) {
      setError('Please fix the validation errors before saving.');
      // Find the first step with errors and switch to it
      const errorFields = Object.keys(validationErrors);
      if (errorFields.some(field => ['firstName', 'lastName', 'professionalType', 'bio', 'npiNumber', 'licenseNumber'].includes(field))) {
        setCurrentStep(1);
      } else if (errorFields.some(field => ['individualRate', 'couplesRate', 'familyRate', 'groupRate'].includes(field))) {
        setCurrentStep(2);
      } else if (errorFields.some(field => ['zipCode', 'serviceRadius'].includes(field))) {
        setCurrentStep(3);
      }
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      // Clean the data before sending (remove UI-only fields)
      const cleanFormData = {
        ...formData,
        serviceAreas: {
          ...formData.serviceAreas
        },
        additionalPhotos: formData.gallery // Map gallery to backend field name
      };
      // Remove the UI-only zipCodesRaw field and gallery field (since we mapped it to additionalPhotos)
      delete cleanFormData.serviceAreas.zipCodesRaw;
      delete cleanFormData.gallery;

      // Ensure professional.licenses is always an array of objects (safety check)
      if (cleanFormData.professional && cleanFormData.professional.licenses) {
        // If licenses is somehow a string, try to parse it
        if (typeof cleanFormData.professional.licenses === 'string') {
          try {
            cleanFormData.professional.licenses = JSON.parse(cleanFormData.professional.licenses);
          } catch (error) {
            console.error('Error parsing licenses string:', error);
            cleanFormData.professional.licenses = [];
          }
        }
        
        // Ensure it's an array and each item has required fields
        if (Array.isArray(cleanFormData.professional.licenses)) {
          cleanFormData.professional.licenses = cleanFormData.professional.licenses.map(license => ({
            licenseType: license.licenseType || license.type || '',
            number: license.number || '',
            state: license.state || '',
            status: license.status || 'active',
            issueDate: license.issueDate || undefined,
            expirationDate: license.expirationDate || undefined
          }));
        } else {
          cleanFormData.professional.licenses = [];
        }
      }

      const method = therapist ? 'PUT' : 'POST';
      const response = await fetch(`${API_BASE_URL}/api/therapists/profile`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...cleanFormData,
          rates: {
            ...formData.rates,
            individual: parseFloat(formData.rates.individual) || undefined,
            couples: parseFloat(formData.rates.couples) || undefined,
            family: parseFloat(formData.rates.family) || undefined,
            group: parseFloat(formData.rates.group) || undefined,
            freeConsultation: {
              offered: formData.rates.freeConsultation.offered
            }
          }
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific validation errors
        if (response.status === 400 && data.errors) {
          const errorMessages = data.errors.map(err => `${err.path || err.param}: ${err.msg}`).join(', ');
          throw new Error(`Validation failed: ${errorMessages}`);
        } else if (response.status === 401) {
          // Handle authentication errors - redirect to login
          localStorage.removeItem('therapylist_token');
          localStorage.removeItem('therapylist_user');
          setError('Session expired. Redirecting to login...');
          setTimeout(() => {
            navigate('/login');
          }, 2000);
          return;
        }
        throw new Error(data.message || 'Failed to save profile');
      }

      setTherapist(data.therapist);
      setIsEditing(false);
      setSuccess('Profile updated successfully!');
      setValidationErrors({});
    } catch (error) {
      console.error('Save profile error:', error);
      setError(error.message || 'Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      if (token) {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('therapylist_token');
      localStorage.removeItem('therapylist_user');
      localStorage.removeItem('token');
      navigate('/');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSaveAndNext = async () => {
    // First save the current changes
    const saveSuccess = await handleSaveWithReturn();
    
    // Only proceed to next step if save was successful
    if (saveSuccess && currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSaveWithReturn = async (skipValidation = false) => {
    // Validate only the current step (unless validation was already done)
    if (!skipValidation && !validateCurrentStep()) {
      setError('Please fix the validation errors before continuing.');
      return false;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      // Clean the data before sending (remove UI-only fields and empty location fields for non-step-3 saves)
      const cleanFormData = {
        ...formData,
        serviceAreas: {
          ...formData.serviceAreas
        },
        additionalPhotos: formData.gallery // Map gallery to backend field name
      };
      // Remove the UI-only zipCodesRaw field and gallery field (since we mapped it to additionalPhotos)
      delete cleanFormData.serviceAreas.zipCodesRaw;
      delete cleanFormData.gallery;

      // For step-specific saves, don't send empty location data that might trigger backend validation
      if (currentStep < 3) {
        // Only send location data if it has meaningful values, otherwise send undefined to avoid backend validation
        if (!cleanFormData.location.address && !cleanFormData.location.city && !cleanFormData.location.state && !cleanFormData.location.zipCode) {
          cleanFormData.location = undefined;
        } else {
          // Remove empty zipCode to avoid backend validation errors
          if (!cleanFormData.location.zipCode || cleanFormData.location.zipCode.trim() === '') {
            delete cleanFormData.location.zipCode;
          }
        }
      }

      // Ensure professional.licenses is always an array of objects (safety check)
      if (cleanFormData.professional && cleanFormData.professional.licenses) {
        // If licenses is somehow a string, try to parse it
        if (typeof cleanFormData.professional.licenses === 'string') {
          try {
            cleanFormData.professional.licenses = JSON.parse(cleanFormData.professional.licenses);
          } catch (error) {
            console.error('Error parsing licenses string:', error);
            cleanFormData.professional.licenses = [];
          }
        }
        
        // Ensure it's an array and each item has required fields
        if (Array.isArray(cleanFormData.professional.licenses)) {
          cleanFormData.professional.licenses = cleanFormData.professional.licenses.map(license => ({
            licenseType: license.licenseType || license.type || '',
            number: license.number || '',
            state: license.state || '',
            status: license.status || 'active',
            issueDate: license.issueDate || undefined,
            expirationDate: license.expirationDate || undefined
          }));
        } else {
          cleanFormData.professional.licenses = [];
        }
      }

      const method = therapist ? 'PUT' : 'POST';
      const response = await fetch(`${API_BASE_URL}/api/therapists/profile`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...cleanFormData,
          rates: {
            ...formData.rates,
            individual: parseFloat(formData.rates.individual) || undefined,
            couples: parseFloat(formData.rates.couples) || undefined,
            family: parseFloat(formData.rates.family) || undefined,
            group: parseFloat(formData.rates.group) || undefined,
            freeConsultation: {
              offered: formData.rates.freeConsultation.offered
            }
          }
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific validation errors
        if (response.status === 400 && data.errors) {
          const errorMessages = data.errors.map(err => `${err.path || err.param}: ${err.msg}`).join(', ');
          
          // Check if the error is related to location fields when not on step 3
          if (currentStep < 3 && errorMessages.includes('location.zipCode')) {
            // Skip location validation errors when not on the location step
            console.warn('Ignoring location validation error on step', currentStep, ':', errorMessages);
            setSuccess('Profile updated successfully!');
            setValidationErrors({});
            return true;
          }
          
          throw new Error(`Validation failed: ${errorMessages}`);
        } else if (response.status === 401) {
          // Handle authentication errors - redirect to login
          localStorage.removeItem('therapylist_token');
          localStorage.removeItem('therapylist_user');
          setError('Session expired. Redirecting to login...');
          setTimeout(() => {
            navigate('/login');
          }, 2000);
          return false;
        }
        throw new Error(data.message || 'Failed to save profile');
      }

      setTherapist(data.therapist);
      setSuccess('Profile updated successfully!');
      setValidationErrors({});
      return true;
    } catch (error) {
      console.error('Save profile error:', error);
      setError(error.message || 'Failed to save profile. Please try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const scrollToEmbed = () => {
    const scroll = () => {
      document.getElementById('embed-logo-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    if (activeTab !== 'overview') {
      setActiveTab('overview');
      setTimeout(scroll, 100);
    } else {
      scroll();
    }
  };

  const dismissEmbedPromo = () => {
    setShowEmbedPromo(false);
    localStorage.setItem('therapylist_embed_promo_seen', '1');
  };

  const inviteUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://therapylist.com'}/for-therapists`;

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setInviteLinkCopied(true);
      setTimeout(() => setInviteLinkCopied(false), 2000);
    } catch (err) {
      // Clipboard not available; ignore silently
    }
  };

  const inviteEmailHref = `mailto:?subject=${encodeURIComponent('Thought of you — TherapyList')}&body=${encodeURIComponent(`Hey,\n\nI'm listed on TherapyList and it's been a great way to connect with clients looking for the right fit. Thought you might want to check it out for your practice:\n\n${inviteUrl}\n\nBest,`)}`;

  const getCurrentStepComponent = () => {
    const step = steps.find(s => s.id === currentStep);
    if (!step) return null;

    const Component = step.component;
    return (
      <Component
        formData={formData}
        setFormData={setFormData}
        isEditing={isEditing}
        validationErrors={validationErrors}
        setValidationErrors={setValidationErrors}
        error={error}
        setError={setError}
        success={success}
        setSuccess={setSuccess}
        onAutoSave={handleSaveWithReturn}
      />
    );
  };

  // Filter inquiries based on search and filter
  const filteredInquiries = inquiries.filter(inquiry => {
    const matchesSearch = !inquirySearchTerm || 
      inquiry.clientInfo?.name?.toLowerCase().includes(inquirySearchTerm.toLowerCase()) ||
      inquiry.clientInfo?.email?.toLowerCase().includes(inquirySearchTerm.toLowerCase()) ||
      inquiry.inquiryDetails?.message?.toLowerCase().includes(inquirySearchTerm.toLowerCase());
    
    const matchesFilter = inquiryFilter === 'all' || inquiry.status === inquiryFilter;
    
    return matchesSearch && matchesFilter;
  });

  // State for analytics data
  const [analyticsData, setAnalyticsData] = useState(null);
  const [monthlyInquiries, setMonthlyInquiries] = useState([]);
  const [websiteVisits, setWebsiteVisits] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Load analytics data from API
  const loadAnalyticsData = async () => {
    if (!therapist) return;

    setAnalyticsLoading(true);
    try {
      const token = localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      // Fetch all analytics data in parallel
      const [overviewResponse, monthlyInquiriesResponse, websiteVisitsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/analytics/therapist/${therapist._id}/overview`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/analytics/therapist/${therapist._id}/monthly-inquiries?months=6`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/analytics/therapist/${therapist._id}/monthly-website-visits?months=6`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (overviewResponse.ok) {
        const overviewData = await overviewResponse.json();
        setAnalyticsData(overviewData.data);
      }

      if (monthlyInquiriesResponse.ok) {
        const monthlyData = await monthlyInquiriesResponse.json();
        setMonthlyInquiries(monthlyData.data.monthlyInquiries);
      }

      if (websiteVisitsResponse.ok) {
        const websiteData = await websiteVisitsResponse.json();
        setWebsiteVisits(websiteData.data.summary);
      }

    } catch (error) {
      console.error('Failed to load analytics data:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Get analytics data for UI
  // eslint-disable-next-line no-unused-vars
  const getAnalyticsData = () => {
    if (!therapist || !analyticsData) return null;
    
    return {
      profileViews: analyticsData.profileViews || 0,
      contactsReceived: analyticsData.inquiries?.total || 0,
      contactsResponded: analyticsData.inquiries?.responded || 0,
      responseRate: analyticsData.responseRate || 0,
      profileCompleteness: analyticsData.profileCompleteness || 0,
      newInquiries: analyticsData.inquiries?.new || 0,
      thisWeekViews: Math.floor((analyticsData.profileViews || 0) * 0.3), // Estimate
      growth: analyticsData.profileViews > 10 ? '+12%' : 'New',
      monthlyInquiries: monthlyInquiries,
      websiteVisits: {
        total: websiteVisits?.totalWebsiteClicks || 0,
        thisMonth: websiteVisits?.thisMonth || 0,
        lastMonth: websiteVisits?.lastMonth || 0,
        growth: websiteVisits?.growth || '0%'
      }
    };
  };



  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#e4fde1] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#254e70] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#254e70]">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#e4fde1]">
      {/* Clean Header Following Brand Guidelines */}
      <div className="bg-white shadow-sm border-b border-[#254e70]/10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#254e70] rounded-lg flex items-center justify-center text-white font-medium text-lg" style={{ fontFamily: 'Kanit' }}>
                {formData.firstName?.[0]}{formData.lastName?.[0]}
              </div>
              <div>
                <h1 className="text-3xl font-medium text-[#254e70] tracking-tight" style={{ fontFamily: 'Kanit' }}>Welcome back, {formData.firstName}</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-[#254e70]" style={{ fontFamily: 'Nunito Sans' }}>
                  {formData.firstName} {formData.lastName}
                </p>
                <p className="text-xs text-[#254e70]/70" style={{ fontFamily: 'Nunito Sans' }}>{formData.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-[#254e70] hover:bg-[#e4fde1] rounded-lg transition-all duration-200 border border-[#254e70]/20 hover:border-[#254e70]"
                style={{ fontFamily: 'Nunito Sans' }}
              >
                <span className="hidden sm:inline">Logout</span>
                <span className="sm:hidden">👋</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Status Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm">⚠️</span>
              </div>
              <p className="text-red-700 font-medium" style={{ fontFamily: 'Nunito Sans' }}>{error}</p>
            </div>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm">✅</span>
              </div>
              <p className="text-green-700 font-medium" style={{ fontFamily: 'Nunito Sans' }}>{success}</p>
            </div>
          </div>
        )}

        {/* Analytics Dashboard */}
        {therapist && !isEditing && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Profile Completeness */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-[#254e70]/10 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-[#254e70] rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"/>
                    <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"/>
                  </svg>
                </div>
                <span className="text-2xl font-semibold text-[#254e70]" style={{ fontFamily: 'Kanit' }}>
                  {analyticsLoading ? '...' : `${analyticsData?.profileCompleteness || therapist.analytics?.profileCompleteness || 0}%`}
                </span>
              </div>
              <h3 className="font-medium text-[#254e70] mb-2" style={{ fontFamily: 'Kanit' }}>Profile Complete</h3>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div 
                  className="bg-[#9dacff] h-2 rounded-full transition-all duration-500"
                  style={{ width: `${analyticsData?.profileCompleteness || therapist.analytics?.profileCompleteness || 0}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>
                {(analyticsData?.profileCompleteness || therapist.analytics?.profileCompleteness || 0) < 80 ? 'Keep building your profile' : 'Great job!'}
              </p>
            </div>

            {/* Profile Views */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-[#254e70]/10 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-[#9dacff] rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                  </svg>
                </div>
                <span className="text-2xl font-semibold text-[#254e70]" style={{ fontFamily: 'Kanit' }}>
                  {analyticsLoading ? '...' : (analyticsData?.profileViews || therapist.analytics?.profileViews || 0)}
                </span>
              </div>
              <h3 className="font-medium text-[#254e70] mb-2" style={{ fontFamily: 'Kanit' }}>Profile Views</h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>
                  This week: {analyticsLoading ? '...' : Math.floor((analyticsData?.profileViews || therapist.analytics?.profileViews || 0) * 0.3)}
                </span>
                <span className="text-xs bg-[#e4fde1] text-[#254e70] px-2 py-1 rounded-full font-medium">
                  {(analyticsData?.profileViews || therapist.analytics?.profileViews || 0) > 10 ? '+12%' : 'New'}
                </span>
              </div>
            </div>

            {/* Website Visits */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-[#254e70]/10 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-[#254e70] rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd"/>
                  </svg>
                </div>
                <span className="text-2xl font-semibold text-[#254e70]" style={{ fontFamily: 'Kanit' }}>
                  {analyticsLoading ? '...' : (websiteVisits?.totalWebsiteClicks || 0)}
                </span>
              </div>
              <h3 className="font-medium text-[#254e70] mb-2" style={{ fontFamily: 'Kanit' }}>Website Visits</h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>
                  This month: {analyticsLoading ? '...' : (websiteVisits?.thisMonth || 0)}
                </span>
                <span className="text-xs bg-[#e4fde1] text-[#254e70] px-2 py-1 rounded-full font-medium">
                  {websiteVisits?.growth || '0%'}
                </span>
              </div>
            </div>

            {/* Response Rate */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-[#254e70]/10 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-[#9dacff] rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/>
                  </svg>
                </div>
                <span className="text-2xl font-semibold text-[#254e70]" style={{ fontFamily: 'Kanit' }}>
                  {analyticsLoading ? '...' : `${analyticsData?.responseRate || 0}%`}
                </span>
              </div>
              <h3 className="font-medium text-[#254e70] mb-2" style={{ fontFamily: 'Kanit' }}>Response Rate</h3>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-[#9dacff] h-2 rounded-full transition-all duration-500"
                  style={{ width: `${analyticsData?.responseRate || 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        {!isEditing && therapist && (
          <div className="bg-white rounded-lg p-6 shadow-sm border border-[#254e70]/10 mb-8">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
              {/* Segmented Tabs */}
              <div className="inline-flex items-center bg-[#e4fde1] rounded-full p-1" role="tablist">
                <button
                  onClick={() => setActiveTab('overview')}
                  role="tab"
                  aria-selected={activeTab === 'overview'}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                    activeTab === 'overview'
                      ? 'bg-[#254e70] text-white shadow-sm'
                      : 'text-[#254e70]/70 hover:text-[#254e70]'
                  }`}
                  style={{ fontFamily: 'Nunito Sans' }}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"/>
                    <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"/>
                  </svg>
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('inquiries')}
                  role="tab"
                  aria-selected={activeTab === 'inquiries'}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 relative flex items-center gap-2 ${
                    activeTab === 'inquiries'
                      ? 'bg-[#254e70] text-white shadow-sm'
                      : 'text-[#254e70]/70 hover:text-[#254e70]'
                  }`}
                  style={{ fontFamily: 'Nunito Sans' }}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd"/>
                  </svg>
                  Inquiries
                  {inquiries.filter(i => i.status === 'new').length > 0 && (
                    <span className="ml-1 bg-red-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center font-bold">
                      {inquiries.filter(i => i.status === 'new').length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  role="tab"
                  aria-selected={activeTab === 'settings'}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                    activeTab === 'settings'
                      ? 'bg-[#254e70] text-white shadow-sm'
                      : 'text-[#254e70]/70 hover:text-[#254e70]'
                  }`}
                  style={{ fontFamily: 'Nunito Sans' }}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/>
                  </svg>
                  Settings
                </button>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative" ref={inviteMenuRef}>
                  <button
                    onClick={() => setInviteMenuOpen((o) => !o)}
                    aria-haspopup="menu"
                    aria-expanded={inviteMenuOpen}
                    className="px-5 py-2 text-sm border border-[#254e70]/30 text-[#254e70] rounded-full hover:bg-[#254e70]/5 transition-all duration-200 flex items-center gap-2 font-medium"
                    style={{ fontFamily: 'Nunito Sans' }}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                    </svg>
                    Invite
                    <svg className={`w-3 h-3 transition-transform duration-200 ${inviteMenuOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                    </svg>
                  </button>
                  {inviteMenuOpen && (
                    <div
                      role="menu"
                      className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-[#254e70]/10 overflow-hidden z-20"
                    >
                      <div className="px-4 py-3 border-b border-[#254e70]/10">
                        <p className="text-sm font-semibold text-[#254e70]" style={{ fontFamily: 'Kanit' }}>
                          Invite a colleague
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5" style={{ fontFamily: 'Nunito Sans' }}>
                          Share TherapyList with another clinician.
                        </p>
                      </div>
                      <button
                        role="menuitem"
                        onClick={() => { copyInviteLink(); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#254e70] hover:bg-[#e4fde1]/60 transition-colors duration-150 text-left"
                        style={{ fontFamily: 'Nunito Sans' }}
                      >
                        {inviteLinkCopied ? (
                          <>
                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                            </svg>
                            <span className="font-medium">Link copied</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd"/>
                            </svg>
                            <span>Copy invite link</span>
                          </>
                        )}
                      </button>
                      <a
                        role="menuitem"
                        href={inviteEmailHref}
                        onClick={() => setInviteMenuOpen(false)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#254e70] hover:bg-[#e4fde1]/60 transition-colors duration-150"
                        style={{ fontFamily: 'Nunito Sans' }}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                        </svg>
                        <span>Send email</span>
                      </a>
                    </div>
                  )}
                </div>
                {therapist && therapist.slug && (
                  <a
                    href={`/therapist/${therapist.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-5 py-2 text-sm border border-[#254e70]/30 text-[#254e70] rounded-full hover:bg-[#254e70]/5 transition-all duration-200 flex items-center gap-2 font-medium"
                    style={{ fontFamily: 'Nunito Sans' }}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                    </svg>
                    View Public Profile
                  </a>
                )}
                <button
                  onClick={scrollToEmbed}
                  className="px-5 py-2 text-sm bg-[#9dacff] text-white rounded-full hover:bg-[#8a9aff] transition-all duration-200 flex items-center gap-2 font-medium shadow-sm hover:shadow-md"
                  style={{ fontFamily: 'Nunito Sans' }}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.25 2A2.25 2.25 0 002 4.25v11.5A2.25 2.25 0 004.25 18h11.5A2.25 2.25 0 0018 15.75V4.25A2.25 2.25 0 0015.75 2H4.25zm4.03 6.28a.75.75 0 00-1.06-1.06L4.97 9.47a.75.75 0 000 1.06l2.25 2.25a.75.75 0 001.06-1.06L6.56 10l1.72-1.72zm4.5-1.06a.75.75 0 10-1.06 1.06L13.44 10l-1.72 1.72a.75.75 0 101.06 1.06l2.25-2.25a.75.75 0 000-1.06l-2.25-2.25z" clipRule="evenodd"/>
                  </svg>
                  Add to Website
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-5 py-2 text-sm bg-[#254e70] text-white rounded-full hover:bg-[#1d3e5a] transition-all duration-200 flex items-center gap-2 font-medium shadow-sm hover:shadow-md"
                  style={{ fontFamily: 'Nunito Sans' }}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
                  </svg>
                  Edit Profile
                </button>
              </div>
            </div>

            {/* Current Status */}
            <div className="flex flex-wrap items-center gap-2 mt-6 pt-6 border-t border-[#254e70]/10">
              <span className="text-xs font-semibold uppercase tracking-wide text-[#254e70]/60 mr-2" style={{ fontFamily: 'Nunito Sans' }}>
                Current Status
              </span>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#e4fde1] rounded-full border border-[#254e70]/10">
                <span className={`w-2 h-2 rounded-full ${therapist?.isActive ? 'bg-green-500' : 'bg-red-500'}`}></span>
                <span className="text-xs font-medium text-[#254e70]" style={{ fontFamily: 'Nunito Sans' }}>
                  Profile: {therapist?.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#e4fde1] rounded-full border border-[#254e70]/10">
                <span className={`w-2 h-2 rounded-full ${therapist?.verification?.isVerified ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                <span className="text-xs font-medium text-[#254e70]" style={{ fontFamily: 'Nunito Sans' }}>
                  {therapist?.verification?.isVerified ? 'Verified' : 'Verification Pending'}
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#e4fde1] rounded-full border border-[#254e70]/10">
                <span className={`w-2 h-2 rounded-full ${therapist?.acceptingNewClients ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                <span className="text-xs font-medium text-[#254e70]" style={{ fontFamily: 'Nunito Sans' }}>
                  {therapist?.acceptingNewClients ? 'Accepting New Clients' : 'Not Accepting New Clients'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Profile Management Header (only when editing) */}
        {isEditing && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10 mb-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#254e70] rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#254e70]" style={{ fontFamily: 'Kanit, sans-serif' }}>
                    Editing Profile
                  </h2>
                  <p className="text-sm text-[#254e70]/70" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                    Use "Save & Next" to progress through sections
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setCurrentStep(1);
                    if (therapist) populateFormData(therapist);
                    setValidationErrors({});
                  }}
                  className="px-4 py-2 text-[#254e70] border border-[#254e70]/30 rounded-lg hover:bg-[#254e70]/5 transition-colors duration-200 font-medium"
                  disabled={isSaving}
                  style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                >
                  Cancel
                </button>

              </div>
            </div>
          </div>
        )}

        {/* Step Navigation */}
        {isEditing && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-medium text-[#254e70]" style={{ fontFamily: 'Kanit, sans-serif' }}>
                Complete Your Profile
              </h3>
              <span className="text-sm text-[#254e70]/70 font-medium" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                Step {currentStep} of {steps.length}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div 
                className="bg-[#9dacff] h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / steps.length) * 100}%` }}
              ></div>
            </div>

            {/* Step Indicators */}
            <div className="flex justify-between items-center">
              {steps.map((step) => (
                <div 
                  key={step.id}
                  className={`flex items-center gap-3 cursor-pointer transition-all duration-200 ${
                    currentStep === step.id 
                      ? 'text-[#254e70]' 
                      : currentStep > step.id 
                        ? 'text-[#9dacff]' 
                        : 'text-gray-400'
                  }`}
                  onClick={() => setCurrentStep(step.id)}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-medium border-2 ${
                    currentStep === step.id 
                      ? 'bg-[#254e70] text-white border-[#254e70]' 
                      : currentStep > step.id 
                        ? 'bg-[#9dacff] text-white border-[#9dacff]' 
                        : 'bg-gray-100 text-gray-400 border-gray-300'
                  }`}>
                    {currentStep > step.id ? '✓' : step.icon}
                  </div>
                  <div className="hidden md:block">
                    <div className="font-medium text-sm">{step.title}</div>
                    <div className="text-xs opacity-70">{step.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step Content */}
        {isEditing ? (
          <div className="space-y-4">
            {getCurrentStepComponent()}
            
            {/* Navigation Buttons */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10">
              <div className="flex justify-between items-center">
                <button
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  className="px-5 py-2 text-[#254e70] border border-[#254e70]/30 rounded-lg hover:bg-[#254e70]/5 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                >
                  ← Previous
                </button>
                
                <div className="flex items-center gap-3">
                  {/* Cancel Button */}
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setCurrentStep(1);
                      if (therapist) populateFormData(therapist);
                      setValidationErrors({});
                      setError('');
                      setSuccess('');
                    }}
                    className="px-4 py-2 text-[#254e70] border border-[#254e70]/30 rounded-lg hover:bg-[#254e70]/5 transition-colors duration-200 font-medium"
                    disabled={isSaving}
                    style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                  >
                    Cancel
                  </button>

                  {currentStep < steps.length ? (
                    <button
                      onClick={handleSaveAndNext}
                      disabled={isSaving}
                      className="px-6 py-2 bg-gradient-to-r from-[#254e70] to-[#1d3e5a] text-white rounded-lg hover:from-[#1d3e5a] hover:to-[#254e70] transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none disabled:shadow-none"
                      style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                    >
                      {isSaving ? (
                        <div className="flex items-center gap-2">
                          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Save & Next →
                        </div>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        // For final completion, validate the entire form
                        if (!validateForm()) {
                          setError('Please complete all required fields across all sections before finalizing your profile.');
                          // Find the first step with errors and switch to it
                          const errorFields = Object.keys(validationErrors);
                          if (errorFields.some(field => ['firstName', 'lastName', 'professionalType', 'email', 'phone', 'bio', 'npiNumber'].includes(field))) {
                            setCurrentStep(1);
                          } else if (errorFields.some(field => ['individualRate', 'couplesRate', 'familyRate', 'groupRate'].includes(field))) {
                            setCurrentStep(2);
                          } else if (errorFields.some(field => ['zipCode', 'serviceRadius'].includes(field))) {
                            setCurrentStep(3);
                          }
                          return;
                        }
                        
                        const success = await handleSaveWithReturn(true); // Skip validation since we already validated
                        if (success) {
                          setIsEditing(false);
                          setSuccess('Profile completed successfully!');
                        }
                      }}
                      disabled={isSaving}
                      className="px-6 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none disabled:shadow-none"
                      style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                    >
                      {isSaving ? (
                        <div className="flex items-center gap-2">
                          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Completing...
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Save & Complete Profile
                        </div>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Tab Content (when not editing)
          <div>
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 gap-6">
                {/* Monthly Inquiries Chart */}
                {monthlyInquiries.length > 0 && (
                  <div className="bg-white rounded-lg p-6 shadow-sm border border-[#254e70]/10">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-[#254e70] rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-xl font-medium text-[#254e70]" style={{ fontFamily: 'Kanit' }}>Monthly Inquiries</h3>
                        <p className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>Track your inquiry trends over time</p>
                      </div>
                    </div>
                    
                    {analyticsLoading ? (
                      <div className="text-center py-12">
                        <div className="w-8 h-8 border-4 border-[#254e70] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>Loading analytics...</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-end justify-between h-48 gap-4">
                          {monthlyInquiries.map((data, index) => {
                            const maxInquiries = Math.max(...monthlyInquiries.map(d => d.inquiries));
                            const height = maxInquiries > 0 ? (data.inquiries / maxInquiries) * 160 : 0;
                            
                            return (
                              <div key={`${data.month}-${data.year}`} className="flex flex-col items-center flex-1">
                                <div className="flex flex-col items-center">
                                  <span className="text-xs font-medium text-[#254e70] mb-2" style={{ fontFamily: 'Nunito Sans' }}>
                                    {data.inquiries}
                                  </span>
                                  <div 
                                    className="bg-[#9dacff] rounded-t-md w-full min-h-[8px] transition-all duration-500"
                                    style={{ height: `${Math.max(height, 8)}px` }}
                                  ></div>
                                </div>
                                <span className="text-xs text-gray-600 mt-2 font-medium" style={{ fontFamily: 'Nunito Sans' }}>
                                  {data.month}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        
                        <div className="mt-6 p-4 bg-[#e4fde1] rounded-lg">
                          <p className="text-sm text-[#254e70]" style={{ fontFamily: 'Nunito Sans' }}>
                            <strong>Trend Analysis:</strong> {analyticsData?.inquiries?.total > 0 ? 
                              `You've received ${analyticsData.inquiries.total} total inquiries. ${analyticsData.inquiries.new > 0 ? `${analyticsData.inquiries.new} new inquiries are waiting for your response.` : 'All inquiries have been addressed.'}` :
                              'No inquiries yet. Keep optimizing your profile to attract more clients.'}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Embeddable Profile Logo */}
                <div id="embed-logo-card" className="scroll-mt-24">
                  <EmbedLogoCard slug={therapist?.slug} />
                </div>

                {/* Profile Optimization Tips */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-[#254e70]/10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#254e70] to-[#1d3e5a] rounded-xl flex items-center justify-center shadow-md">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-[#254e70]" style={{ fontFamily: 'Kanit' }}>Profile Optimization Tips</h3>
                      <p className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>Suggestions to help your profile attract more clients</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(analyticsData?.profileCompleteness || therapist.analytics?.profileCompleteness || 0) < 100 && (
                      <div className="p-4 bg-[#e4fde1] rounded-lg border border-[#254e70]/20">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[#254e70]">📝</span>
                          <span className="font-medium text-[#254e70]" style={{ fontFamily: 'Nunito Sans' }}>Complete Your Profile</span>
                        </div>
                        <p className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>
                          Your profile is {analyticsData?.profileCompleteness || therapist.analytics?.profileCompleteness || 0}% complete. Add more details to improve visibility.
                        </p>
                      </div>
                    )}

                    {!therapist?.profilePhoto && (
                      <div className="p-4 bg-[#e4fde1] rounded-lg border border-[#254e70]/20">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[#9dacff]">📸</span>
                          <span className="font-medium text-[#254e70]" style={{ fontFamily: 'Nunito Sans' }}>Add Profile Photo</span>
                        </div>
                        <p className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>
                          Profiles with photos get 40% more views and inquiries.
                        </p>
                      </div>
                    )}

                    {(analyticsData?.responseRate || 0) < 50 && (analyticsData?.inquiries?.total || 0) > 0 && (
                      <div className="p-4 bg-[#e4fde1] rounded-lg border border-[#254e70]/20">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/>
                          </svg>
                          <span className="font-medium text-[#254e70]" style={{ fontFamily: 'Nunito Sans' }}>Improve Response Rate</span>
                        </div>
                        <p className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>
                          Respond to inquiries within 24 hours to improve your response rate.
                        </p>
                      </div>
                    )}

                    {(analyticsData?.profileCompleteness || therapist.analytics?.profileCompleteness || 0) >= 100 && therapist?.profilePhoto && (analyticsData?.responseRate || 0) >= 80 && (
                      <div className="p-4 bg-[#e4fde1] rounded-lg border border-[#254e70]/20">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-green-600">🎉</span>
                          <span className="font-medium text-[#254e70]" style={{ fontFamily: 'Nunito Sans' }}>Excellent Profile!</span>
                        </div>
                        <p className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>
                          Your profile is optimized and performing well. Keep up the great work!
                        </p>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            )}

            {activeTab === 'inquiries' && (
              <div className="space-y-6">
                {/* Inquiry Management Header */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-[#254e70]/10">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#254e70] rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-xl font-medium text-[#254e70]" style={{ fontFamily: 'Kanit' }}>Client Inquiries</h3>
                        <p className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>Manage and respond to client messages</p>
                      </div>
                    </div>
                    <button
                      onClick={loadInquiries}
                      disabled={isLoadingInquiries}
                      className="px-4 py-2 bg-[#254e70] text-white rounded-lg hover:bg-[#1d3e5a] transition-all duration-200 disabled:opacity-50 flex items-center gap-2 shadow-sm hover:shadow-md"
                      style={{ fontFamily: 'Nunito Sans' }}
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd"/>
                      </svg>
                      {isLoadingInquiries ? 'Loading...' : 'Refresh'}
                    </button>
                  </div>

                  {/* Search and Filter Controls */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search inquiries by name, email, or message..."
                          value={inquirySearchTerm}
                          onChange={(e) => setInquirySearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-[#254e70]/20 rounded-lg focus:ring-2 focus:ring-[#9dacff] focus:border-[#254e70] transition-all duration-200"
                          style={{ fontFamily: 'Nunito Sans' }}
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="w-4 h-4 text-[#254e70]/60" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div>
                      <select
                        value={inquiryFilter}
                        onChange={(e) => setInquiryFilter(e.target.value)}
                        className="px-4 py-2 border border-[#254e70]/20 rounded-lg focus:ring-2 focus:ring-[#9dacff] focus:border-[#254e70] transition-all duration-200"
                        style={{ fontFamily: 'Nunito Sans' }}
                      >
                        <option value="all">All Inquiries</option>
                        <option value="new">New</option>
                        <option value="responded">Responded</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  {inquiries.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                      <div className="text-center p-3 bg-[#e4fde1] rounded-lg border border-[#254e70]/20">
                        <div className="text-2xl font-semibold text-[#254e70]" style={{ fontFamily: 'Kanit' }}>{inquiries.length}</div>
                        <div className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>Total</div>
                      </div>
                      <div className="text-center p-3 bg-[#e4fde1] rounded-lg border border-[#254e70]/20">
                        <div className="text-2xl font-semibold text-[#254e70]" style={{ fontFamily: 'Kanit' }}>{inquiries.filter(i => i.status === 'new').length}</div>
                        <div className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>New</div>
                      </div>
                      <div className="text-center p-3 bg-[#e4fde1] rounded-lg border border-[#254e70]/20">
                        <div className="text-2xl font-semibold text-[#254e70]" style={{ fontFamily: 'Kanit' }}>{inquiries.filter(i => i.status === 'responded').length}</div>
                        <div className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>Responded</div>
                      </div>
                      <div className="text-center p-3 bg-[#e4fde1] rounded-lg border border-[#254e70]/20">
                        <div className="text-2xl font-semibold text-[#254e70]" style={{ fontFamily: 'Kanit' }}>{inquiries.filter(i => i.status === 'closed').length}</div>
                        <div className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>Closed</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Inquiry List */}
                <div className="bg-white rounded-lg shadow-sm border border-[#254e70]/10 overflow-hidden">
                  {isLoadingInquiries ? (
                    <div className="text-center py-12">
                      <div className="w-8 h-8 border-4 border-[#254e70] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                      <p className="text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>Loading inquiries...</p>
                    </div>
                  ) : filteredInquiries.length === 0 ? (
                    <div className="text-center py-12">
                      {inquirySearchTerm || inquiryFilter !== 'all' ? (
                        <div>
                          <div className="w-16 h-16 bg-[#e4fde1] rounded-lg flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-[#254e70]" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/>
                            </svg>
                          </div>
                          <h4 className="text-lg font-medium text-[#254e70] mb-2" style={{ fontFamily: 'Kanit' }}>No matching inquiries</h4>
                          <p className="text-gray-600 text-sm" style={{ fontFamily: 'Nunito Sans' }}>Try adjusting your search or filter criteria.</p>
                        </div>
                      ) : (
                        <div>
                          <div className="w-16 h-16 bg-[#e4fde1] rounded-lg flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-[#254e70]" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd"/>
                            </svg>
                          </div>
                          <h4 className="text-lg font-medium text-[#254e70] mb-2" style={{ fontFamily: 'Kanit' }}>No inquiries yet</h4>
                          <p className="text-gray-600 text-sm" style={{ fontFamily: 'Nunito Sans' }}>When clients reach out through your profile, their messages will appear here.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="divide-y divide-[#254e70]/10">

                      {filteredInquiries.map((inquiry) => (
                        <div 
                          key={inquiry._id}
                          className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors duration-200"
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="font-medium text-[#254e70] mb-1">
                                {inquiry.clientInfo?.name || 'Anonymous'}
                              </h4>
                              <p className="text-sm text-[#254e70]/70">
                                {inquiry.clientInfo?.email}
                              </p>
                              {inquiry.clientInfo?.phone && (
                                <p className="text-sm text-[#254e70]/70">
                                  📞 {inquiry.clientInfo.phone}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                inquiry.status === 'new' 
                                  ? 'bg-blue-100 text-blue-800'
                                  : inquiry.status === 'responded'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {inquiry.status === 'new' ? 'New' : inquiry.status === 'responded' ? 'Responded' : inquiry.status}
                              </span>
                              <p className="text-xs text-[#254e70]/50 mt-1">
                                {new Date(inquiry.createdAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>

                          <div className="mb-3">
                            <p className="text-sm text-[#254e70] mb-2">
                              <strong>Preferred Contact:</strong> {inquiry.inquiryDetails?.preferredContact || 'Email'}
                            </p>
                            <div className="bg-gray-50 rounded p-3">
                              <p className="text-sm text-[#254e70]">
                                {inquiry.inquiryDetails?.message || 'No message provided'}
                              </p>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            {inquiry.status === 'new' && (
                              <button
                                onClick={() => updateInquiryStatus(inquiry._id, 'responded')}
                                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors duration-200"
                              >
                                Mark as Responded
                              </button>
                            )}
                            <a
                              href={`mailto:${inquiry.clientInfo?.email}?subject=Re: Your inquiry about therapy services`}
                              className="px-3 py-1 bg-[#254e70] text-white rounded text-sm hover:bg-[#1d3e5a] transition-colors duration-200"
                            >
                              Reply via Email
                            </a>
                            {inquiry.clientInfo?.phone && (
                              <a
                                href={`tel:${inquiry.clientInfo.phone}`}
                                className="px-3 py-1 bg-[#9dacff] text-white rounded text-sm hover:bg-[#8a9aff] transition-colors duration-200"
                              >
                                Call
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <AccountSettings />
            )}
          </div>
        )}
      </div>

      {/* One-time embed badge promo */}
      {showEmbedPromo && !isEditing && (
        <div className="fixed bottom-6 right-6 z-50 max-w-md w-[calc(100%-3rem)] sm:w-[26rem] bg-white rounded-xl shadow-2xl border border-[#254e70]/10 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-[#9dacff] to-[#254e70]"></div>
          <button
            onClick={dismissEmbedPromo}
            aria-label="Dismiss"
            className="absolute top-3 right-3 z-10 text-gray-400 hover:text-[#254e70] transition-colors duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#e4fde1] rounded-full text-[10px] font-semibold uppercase tracking-wide text-[#254e70]" style={{ fontFamily: 'Nunito Sans' }}>
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                New
              </span>
            </div>
            <h4 className="font-semibold text-[#254e70] text-lg leading-snug pr-6" style={{ fontFamily: 'Kanit' }}>
              Add a Verified Badge to Your Website
            </h4>
            <p className="text-sm text-gray-600 mt-1.5 leading-relaxed" style={{ fontFamily: 'Nunito Sans' }}>
              Show clients you're a verified TherapyList provider with a one-line embed.
            </p>

            {/* Badge preview */}
            <div
              className="mt-4 flex items-center justify-center p-2 rounded-lg border border-[#254e70]/10"
              style={{ background: '#e4fde1' }}
            >
              <img
                src="https://cms.therapylist.com/wp-content/uploads/2025/07/1-scaled.png"
                alt="TherapyList verified badge"
                style={{ height: '104px', width: 'auto', border: 0, display: 'block' }}
              />
            </div>

            <div className="flex items-center gap-2 mt-5">
              <button
                onClick={() => { scrollToEmbed(); dismissEmbedPromo(); }}
                className="px-4 py-2 text-sm font-medium bg-[#254e70] text-white rounded-full hover:bg-[#1d3e5a] transition-colors duration-200 shadow-sm"
                style={{ fontFamily: 'Nunito Sans' }}
              >
                Get My Badge
              </button>
              <button
                onClick={dismissEmbedPromo}
                className="px-4 py-2 text-sm font-medium text-[#254e70]/70 hover:text-[#254e70] transition-colors duration-200"
                style={{ fontFamily: 'Nunito Sans' }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TherapistDashboard;